import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Loader2, ExternalLink, X, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { getMyBilling } from "@/lib/billing.functions";
import {
  getCustomerPortal,
  changePlan,
  cancelAtPeriodEnd,
  resumeSubscription,
} from "@/lib/payments.functions";
import { supabase } from "@/integrations/supabase/client";
import { PaymentTestModeBanner } from "@/components/payment-test-mode-banner";
import { getPaddleEnvironment } from "@/lib/paddle";
import { toast } from "sonner";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";

import { MODULES, BASE_PLANS, computePrice, type BasePlanId, type BillingCycle } from "@/lib/billing/modules";

export const Route = createFileRoute("/_authenticated/app/billing")({ component: BillingPage });

function BillingPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [user, setUser] = useState<{ id: string; email?: string } | null>(null);
  const [seats, setSeats] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);
  const coLoading = false;
  const env = getPaddleEnvironment();

  const [selectedBase, setSelectedBase] = useState<BasePlanId>("pro");
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [cycle, setCycle] = useState<BillingCycle>("monthly");

  const breakdown = useMemo(
    () => computePrice(selectedBase, selectedModules, cycle),
    [selectedBase, selectedModules, cycle],
  );

  void seats; void setSeats;

  // Sync local config with server state on first load.
  useEffect(() => {
    if (!data) return;
    setSelectedBase((data.plan as BasePlanId) ?? "pro");
    setSelectedModules((data.activeModules as string[]) ?? []);
    setCycle((data.billingCycle as BillingCycle) ?? "monthly");
  }, [data]);

  const toggleModule = (id: string) =>
    setSelectedModules((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));

  const refresh = async () => {
    try {
      const d = await getMyBilling();
      setData(d);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refresh();
    supabase.auth.getUser().then(({ data: { user } }) => user && setUser({ id: user.id, email: user.email }));
  }, []);

  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`billing-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "billing_transactions", filter: `owner_id=eq.${user.id}` }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "profiles", filter: `id=eq.${user.id}` }, refresh)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!new URLSearchParams(window.location.search).get("checkout")) return;
    let n = 0;
    const t = setInterval(() => { refresh(); if (++n >= 10) clearInterval(t); }, 3000);
    return () => clearInterval(t);
  }, []);

  if (!data || !user) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-US" : "it-IT";
  const used = Number(data.used ?? 0);
  const limit = Number(data.limit ?? 0);
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const planName = BASE_PLANS.find((p) => p.id === data.plan)?.name ?? data.plan;
  const sub = data.subscription as null | {
    paddle_subscription_id: string;
    status: string;
    cancel_at_period_end: boolean;
    current_period_end: string | null;
    quantity: number;
  };

  const handleConfirmPurchase = async () => {
    const plan = BASE_PLANS.find((p) => p.id === selectedBase);
    if (!plan) return;
    // Build items: base plan + selected modules (skip those included in the plan).
    const items: { priceId: string; quantity?: number }[] = [];
    if (plan.id !== "starter") {
      const planPrice = cycle === "annual" ? `${plan.id}_plan_annual` : `${plan.id}_plan_monthly`;
      items.push({ priceId: planPrice, quantity: 1 });
    }
    const includedIds = new Set(
      breakdown.selectedModules
        .filter((m) => {
          if (selectedBase === "studio") return true;
          if (selectedBase === "pro" && m.tier === "standard") {
            const idx = breakdown.selectedModules.filter((s) => s.tier === "standard").findIndex((s) => s.id === m.id);
            return idx < (BASE_PLANS.find((p) => p.id === "pro")?.includedStandardModules ?? 0);
          }
          return false;
        })
        .map((m) => m.id),
    );
    for (const m of breakdown.selectedModules) {
      if (includedIds.has(m.id)) continue;
      items.push({ priceId: cycle === "annual" ? m.priceIdAnnual : m.priceIdMonthly, quantity: 1 });
    }
    if (!items.length) {
      toast.info(t("app.billing.selectAtLeastOne"));
      return;
    }
    void items;
    openWhatsAppContact("Ciao Gianluca, vorrei attivare/aggiornare il mio piano LexAI Pro. Possiamo procedere?");
  };
  void changePlan;

  const handlePortal = async () => {
    const r = await getCustomerPortal({ data: { environment: env } });
    if (!r.ok) return toast.error(r.error);
    window.open(r.url, "_blank");
  };

  const handleCancel = async () => {
    if (!confirm(t("app.billing.cancelConfirm"))) return;
    setActionLoading(true);
    try {
      const r = await cancelAtPeriodEnd({ data: { environment: env } });
      if (!r.ok) throw new Error(r.error);
      toast.success(t("app.billing.cancelScheduled"));
      await refresh();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.billing.cannotCancel"));
    } finally { setActionLoading(false); }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      const r = await resumeSubscription({ data: { environment: env } });
      if (!r.ok) throw new Error(r.error);
      toast.success(t("app.billing.restored"));
      await refresh();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.billing.cannotResume"));
    } finally { setActionLoading(false); }
  };

  return (
    <>
      <PaymentTestModeBanner />
      <PageHeader title={t("app.billing.title")} description={t("app.billing.description")} />
      <PageBody>
        <Card className="mb-6 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-sm text-muted-foreground">{t("app.billing.currentPlan")}</div>
              <div className="mt-1 flex items-center gap-2">
                <span className="font-display text-2xl font-bold">{planName}</span>
                <Badge variant={sub?.status === "past_due" ? "destructive" : "secondary"}>
                  {sub?.status === "past_due" ? t("app.billing.statusPastDue") : t("app.billing.statusActive")}
                </Badge>
                {sub?.cancel_at_period_end && (
                  <Badge variant="outline">{t("app.billing.expiringOn", { date: new Date(sub.current_period_end!).toLocaleDateString(locale) })}</Badge>
                )}
              </div>
            </div>
            {data.plan !== "starter" && sub && (
              <div className="flex flex-wrap gap-2">
                {sub.cancel_at_period_end ? (
                  <Button variant="outline" size="sm" onClick={handleResume} disabled={actionLoading}>
                    <RotateCcw className="mr-2 h-4 w-4" /> {t("app.billing.resume")}
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleCancel} disabled={actionLoading}>
                    <X className="mr-2 h-4 w-4" /> {t("app.billing.cancelEow")}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handlePortal}>
                  <ExternalLink className="mr-2 h-4 w-4" /> {t("app.billing.paymentMethod")}
                </Button>
              </div>
            )}
          </div>
          <div className="mt-6">
            <div className="mb-2 flex justify-between text-sm">
              <span className="text-muted-foreground">{t("app.billing.aiQueriesUsed")}</span>
              <span className="font-medium">{used.toLocaleString(locale)} / {limit.toLocaleString(locale)}</span>
            </div>
            <Progress value={pct} />
          </div>
        </Card>

        <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-200">
          <strong>{t("paymentsWip.title")}.</strong> {t("app.billing.paymentsWipNotice")}
        </div>

        <h2 className="mb-3 font-display text-lg font-semibold">{data.plan === "starter" ? t("app.billing.choosePlan") : t("app.billing.changePlan")}</h2>

        {/* Toggle ciclo */}
        <div className="mb-5 flex items-center gap-2">
          <div className="inline-flex rounded-full border bg-muted/40 p-1">
            <button
              type="button"
              onClick={() => setCycle("monthly")}
              className={cn("rounded-full px-4 py-1.5 text-xs font-medium transition", cycle === "monthly" ? "bg-background shadow" : "text-muted-foreground")}
            >{t("app.billing.cycleMonthly")}</button>
            <button
              type="button"
              onClick={() => setCycle("annual")}
              className={cn("rounded-full px-4 py-1.5 text-xs font-medium transition", cycle === "annual" ? "bg-background shadow" : "text-muted-foreground")}
            >{t("app.billing.cycleAnnual")} <span className="ml-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-600">{t("app.billing.annualSave")}</span></button>
          </div>
        </div>

        {/* Piani base */}
        <div className="mb-6 grid gap-4 lg:grid-cols-3">
          {BASE_PLANS.map((p) => {
            const selected = selectedBase === p.id;
            const monthly = cycle === "annual" ? Math.round(p.monthlyPrice * 0.8) : p.monthlyPrice;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedBase(p.id)}
                className={cn(
                  "relative rounded-xl border p-5 text-left transition",
                  selected ? "border-accent ring-2 ring-accent/40" : "hover:border-foreground/30",
                  p.id === "pro" && "lg:scale-[1.01]",
                )}
              >
                {p.id === "pro" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent px-2.5 py-0.5 text-[10px] font-semibold text-accent-foreground">
                    <Sparkles className="mr-1 inline h-3 w-3" />{t("app.billing.mostPopular")}
                  </div>
                )}
                <h3 className="font-display text-lg font-bold">{p.name}</h3>
                <div className="mt-1 font-display text-3xl font-bold">€{monthly}<span className="text-sm font-normal text-muted-foreground">{t("app.billing.perMonth")}</span></div>
                <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-emerald-500" />{t("app.billing.aiQueriesPerMonth", { n: p.aiQueriesPerMonth.toLocaleString(locale) })}</li>
                  <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-emerald-500" />
                    {p.includedStandardModules === -1
                      ? t("app.billing.allModulesIncluded")
                      : p.includedStandardModules > 0
                        ? t("app.billing.modulesIncluded", { count: p.includedStandardModules })
                        : t("app.billing.modulesSoldSeparately")}
                  </li>
                </ul>
              </button>
            );
          })}
        </div>

        {/* Griglia moduli */}
        <h3 className="mb-2 font-display text-base font-semibold">{t("app.billing.specializations")}</h3>
        <p className="mb-3 text-sm text-muted-foreground">
          {t("app.billing.specializationsDesc")}
        </p>
        <div className="mb-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => {
            const on = selectedModules.includes(m.id);
            const includedFree = selectedBase === "studio" || (
              selectedBase === "pro" && m.tier === "standard" &&
              breakdown.selectedModules.filter((s) => s.tier === "standard").findIndex((s) => s.id === m.id) <
                (BASE_PLANS.find((p) => p.id === "pro")?.includedStandardModules ?? 0)
            );
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => toggleModule(m.id)}
                className={cn(
                  "flex items-start justify-between gap-3 rounded-lg border p-3 text-left transition",
                  on ? "border-accent bg-accent/5" : "hover:border-foreground/30",
                )}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium">{m.name}</span>
                    <Badge variant="outline" className="h-4 px-1 text-[9px] uppercase">{m.tier}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{m.short}</div>
                </div>
                <div className="shrink-0 text-right">
                  {on && includedFree
                    ? <span className="text-xs font-medium text-emerald-600">{t("app.billing.includedTag")}</span>
                    : <span className="text-sm font-semibold">€{m.monthlyPrice}<span className="text-[10px] font-normal text-muted-foreground">{t("app.billing.perMonth")}</span></span>}
                  <div className={cn("mt-1 inline-block h-4 w-4 rounded border", on ? "bg-accent border-accent" : "border-foreground/30")}>
                    {on && <Check className="h-4 w-4 text-accent-foreground" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Riepilogo + CTA */}
        <Card className="mb-8 p-5">
          <div className="grid gap-4 md:grid-cols-[1fr,auto] md:items-center">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("app.billing.summaryPlan", { name: BASE_PLANS.find((p) => p.id === selectedBase)?.name })}</span><span>€{breakdown.basePrice.toFixed(2)}</span></div>
              {breakdown.modulesPrice > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">{t("app.billing.summaryModules", { n: breakdown.selectedModules.length })}</span><span>€{breakdown.modulesPrice.toFixed(2)}</span></div>
              )}
              {breakdown.includedDiscount > 0 && (
                <div className="flex justify-between text-emerald-600"><span>{t("app.billing.summaryIncludedDiscount")}</span><span>−€{breakdown.includedDiscount.toFixed(2)}</span></div>
              )}
              {breakdown.bundleDiscount > 0 && (
                <div className="flex justify-between text-emerald-600"><span>{t("app.billing.summaryBundleDiscount", { pct: breakdown.bundleDiscountPct })}</span><span>−€{breakdown.bundleDiscount.toFixed(2)}</span></div>
              )}
              {breakdown.cycleDiscount > 0 && (
                <div className="flex justify-between text-emerald-600"><span>{t("app.billing.summaryAnnualDiscount", { pct: breakdown.cycleDiscountPct })}</span><span>−€{breakdown.cycleDiscount.toFixed(2)}</span></div>
              )}
              <div className="mt-2 flex items-baseline justify-between border-t pt-2">
                <span className="font-display text-base font-semibold">{cycle === "annual" ? t("app.billing.summaryTotalMonthlyBilledAnnual") : t("app.billing.summaryTotalMonthly")}</span>
                <span className="font-display text-2xl font-bold">€{breakdown.total.toFixed(2)}<span className="text-xs font-normal text-muted-foreground">{t("app.billing.perMonth")}</span></span>
              </div>
              {cycle === "annual" && (
                <div className="text-right text-xs text-muted-foreground">{t("app.billing.perYearAmount", { amount: breakdown.totalAnnualEquivalent.toFixed(2) })}</div>
              )}
            </div>
            {(() => {
              const currentMods = ((data.activeModules as string[]) ?? []).slice().sort().join(",");
              const selMods = selectedModules.slice().sort().join(",");
              const unchanged = selectedBase === data.plan && currentMods === selMods && cycle === (data.billingCycle ?? "monthly");
              return (
                <Button
                  size="lg"
                  className="md:min-w-[200px]"
                  disabled={actionLoading || coLoading || unchanged}
                  onClick={handleConfirmPurchase}
                >
                  {unchanged ? t("app.billing.currentConfig") : t("app.billing.confirmAndPay")}
                </Button>
              );
            })()}
          </div>
        </Card>

        <h2 className="mb-3 font-display text-lg font-semibold">{t("app.billing.paymentsHistory")}</h2>
        <Card className="overflow-hidden">
          {data.transactions.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">{t("app.billing.noPayments")}</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr><th className="px-4 py-2">{t("app.billing.colDate")}</th><th className="px-4 py-2">{t("app.billing.colDescription")}</th><th className="px-4 py-2">{t("app.billing.colAmount")}</th><th className="px-4 py-2">{t("app.billing.colStatus")}</th><th className="px-4 py-2"></th></tr>
              </thead>
              <tbody>
                {data.transactions.map((tr: any) => (
                  <tr key={tr.id} className="border-t">
                    <td className="px-4 py-2">{new Date(tr.occurred_at).toLocaleDateString(locale)}</td>
                    <td className="px-4 py-2">{tr.description ?? "-"}</td>
                    <td className="px-4 py-2">{(tr.amount_cents / 100).toFixed(2)} {tr.currency}</td>
                    <td className="px-4 py-2"><Badge variant={tr.status === "paid" ? "default" : "secondary"}>{tr.status}</Badge></td>
                    <td className="px-4 py-2">{tr.invoice_url && <a href={tr.invoice_url} target="_blank" rel="noreferrer" className="text-accent underline">{t("app.billing.invoice")}</a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </PageBody>
    </>
  );
}
